import { useEffect, useState, type ReactNode } from "react"
import { Drawer, Layout } from "antd"
import { useQuery } from "@tanstack/react-query"
import { fetchMe } from "../../../features/tiles/api.js"
import { useFilterUrlSync } from "../../../features/filters/use-filter-url-sync.js"
import { GlobalFilterBar } from "../../../features/filters/components/global-filter-bar.js"
import { useTheme } from "../../providers/theme-provider.js"
import { AppSider } from "./app-sider.js"
import { AppTopbar } from "./app-topbar.js"

const NARROW = 992
const COLLAPSE_KEY = "csbi.sider.collapsed"

function useIsNarrow() {
  const [narrow, setNarrow] = useState(() => globalThis.innerWidth < NARROW)
  useEffect(() => {
    const onResize = () => setNarrow(globalThis.innerWidth < NARROW)
    globalThis.addEventListener("resize", onResize)
    return () => globalThis.removeEventListener("resize", onResize)
  }, [])
  return narrow
}

export type AppShellProps = {
  children: ReactNode
  /**
   * ⚠ It used to appear on every page. But only three places in the whole app actually read `useFilterStore`:
   * the home dashboard, `/domain/:slug` and `/dashboards/:slug` (the latter two through `<Tile>`). On every
   * other page, changing the customer, the time range or the granularity **did nothing at all and reported
   * nothing** — another control that changes nothing. `/ops/coverage` was the most misleading: it had both the
   * global filter bar and its own row of filters, the two looking much alike, with only the lower row working.
   */
  showFilters?: boolean
  onRefresh?: () => void
  generatedAt?: string
}

export function AppShell({ children, showFilters, onRefresh, generatedAt }: AppShellProps) {
  useFilterUrlSync()
  const { mode } = useTheme()
  const isNarrow = useIsNarrow()
  const [collapsed, setCollapsed] = useState(() => globalThis.localStorage?.getItem(COLLAPSE_KEY) === "1")
  const [drawerOpen, setDrawerOpen] = useState(false)

  const { data: staff } = useQuery({
    queryKey: ["auth", "me"],
    queryFn: ({ signal }) => fetchMe(signal),
    staleTime: 10 * 60_000,
    retry: false,
  })

  const toggle = () => {
    setCollapsed((v) => {
      globalThis.localStorage?.setItem(COLLAPSE_KEY, v ? "0" : "1")
      return !v
    })
  }

  return (
    <Layout hasSider style={{ minHeight: "100vh", background: "var(--csbi-surface)" }}>
      {isNarrow ? (
        <Drawer
          placement="left"
          width={240}
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          styles={{ body: { padding: 0 } }}
        >
          <AppSider
            collapsed={false}
            capabilities={staff?.capabilities ?? []}
            onNavigate={() => setDrawerOpen(false)}
          />
        </Drawer>
      ) : (
        <Layout.Sider
          theme="light"
          collapsible
          collapsed={collapsed}
          width={240}
          collapsedWidth={80}
          trigger={null}
          style={{
            position: "sticky",
            top: 0,
            height: "100vh",
            overflow: "hidden",
            borderInlineEnd: "1px solid var(--csbi-gridline)",
          }}
        >
          <AppSider collapsed={collapsed} onToggle={toggle} capabilities={staff?.capabilities ?? []} />
        </Layout.Sider>
      )}

      <Layout style={{ background: "var(--csbi-surface)" }}>
        <AppTopbar
          showMenuButton={isNarrow}
          onMenuClick={() => setDrawerOpen(true)}
          {...(staff ? { staff } : {})}
        />
        {showFilters && (
          <div style={{ position: "sticky", top: 0, zIndex: 10, backdropFilter: "blur(6px)" }}>
            <GlobalFilterBar
              mode={mode}
              {...(onRefresh ? { onRefresh } : {})}
              {...(generatedAt ? { generatedAt } : {})}
            />
          </div>
        )}
        <Layout.Content style={{ width: "100%", maxWidth: 1600, margin: "0 auto", padding: "20px 24px 48px" }}>
          {children}
        </Layout.Content>
      </Layout>
    </Layout>
  )
}
